import { Check, Minus } from 'lucide-react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { PLAN_TIERS, type PlanTier } from '@/shared/config/plans';
import { cn } from '@/shared/lib';

import { COMPARISON_ROWS, type ComparisonValue } from '../model/pricingContent';

/** The highlighted column; matches the highlighted plan card. */
const FEATURED: PlanTier = 'pro';

/** Renders one matrix cell: a value, a tick, or a dash. */
const Cell = ({ value }: { value: ComparisonValue }) => {
  const { t } = useTranslation('marketing');

  if (typeof value === 'object') {
    return (
      <span className="text-sm font-bold text-gray-900">
        {t(`pricing.featureValues.${value.valueKey}`, { count: value.count })}
      </span>
    );
  }
  return value ? (
    <Check className="mx-auto h-5 w-5 text-primary" aria-label={t('pricing.included')} />
  ) : (
    <Minus className="mx-auto h-5 w-5 text-gray-300" aria-label={t('pricing.notIncluded')} />
  );
};

/** Category order, taken from first appearance in the row list. */
const CATEGORIES = [...new Set(COMPARISON_ROWS.map((row) => row.categoryKey))];

/**
 * Full plan comparison matrix, generated from the plan matrix.
 *
 * Hidden below the md breakpoint — a thirteen-row, five-column table is not
 * readable on a phone, and the plan cards already carry the headline features.
 */
export const PlanComparisonTable = () => {
  const { t } = useTranslation('marketing');

  return (
    <section className="hidden w-full bg-white pb-24 md:block">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <h2 className="mb-12 text-center font-heading text-3xl font-bold text-dark">
          {t('pricing.comparisonTitle')}
        </h2>

        <div className="overflow-hidden rounded-[16px] border border-gray-100 bg-white shadow-card">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="bg-gray-50">
                <th scope="col" className="w-1/3 px-6 py-5 font-bold text-dark">
                  {t('pricing.comparisonFeatures')}
                </th>
                {PLAN_TIERS.map((tier) => (
                  <th
                    key={tier}
                    scope="col"
                    className={cn(
                      'px-4 py-5 text-center font-bold',
                      tier === FEATURED ? 'bg-primary/5 text-primary' : 'text-dark',
                    )}
                  >
                    {t(`pricing.plans.${tier}.name`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {CATEGORIES.map((category) => (
                <Fragment key={category}>
                  <tr>
                    <th
                      scope="colgroup"
                      colSpan={PLAN_TIERS.length + 1}
                      className="bg-gray-50 px-6 py-4 text-sm font-bold tracking-widest text-gray-900 uppercase"
                    >
                      {t(`pricing.categories.${category}`)}
                    </th>
                  </tr>
                  {COMPARISON_ROWS.filter((row) => row.categoryKey === category).map((row) => (
                    <tr
                      key={row.featureKey}
                      className="border-b border-gray-100 transition-colors last:border-0 hover:bg-gray-50/50"
                    >
                      <th scope="row" className="px-6 py-4 font-medium text-gray-600">
                        {t(`pricing.featureNames.${row.featureKey}`)}
                      </th>
                      {PLAN_TIERS.map((tier) => (
                        <td
                          key={tier}
                          className={cn(
                            'px-4 py-4 text-center',
                            tier === FEATURED && 'bg-primary/[0.02]',
                          )}
                        >
                          <Cell value={row.value(tier)} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};
